import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "./config";
import { getNotificationPoller } from "./notificationsPoll";
import { getEcho } from "./echo";
import { InlineLoader } from "./PageLoader";
import ChatModal from "./ChatModal";
import { alertNewMessage, ensureNotificationPermission } from "./chatNotify";
import {
  HeartIcon,
  ChatBubbleOvalLeftIcon,
  PhotoIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  XMarkIcon,
  BellIcon as BellSolidIcon,
  BookOpenIcon,
  AtSymbolIcon,
} from "@heroicons/react/24/solid";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";

// Small avatar (image or initial) for a notification's actor.
function ActorAvatar({ name, img, primary, size = 36 }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold text-white"
      style={{ width: size, height: size, fontSize: size * 0.4, backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
    >
      {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : initial}
    </span>
  );
}

// A kind-coloured badge overlapping the actor avatar so a like / comment / new
// post / DM reads at a glance (Instagram-style). Uses solid Heroicons so the
// glyphs are crisp and premium at badge size.
const NOTIF_GLYPH = {
  like_post: { bg: "#f43f5e", Icon: HeartIcon },
  like_comment: { bg: "#f43f5e", Icon: HeartIcon },
  comment_post: { bg: "#3b82f6", Icon: ChatBubbleOvalLeftIcon },
  // @[Name](id) tag in a group post or comment.
  mention: { bg: "#8b5cf6", Icon: AtSymbolIcon },
  message: { brand: true, Icon: ChatBubbleLeftRightIcon },
  new_post: { brand: true, Icon: PhotoIcon },
  approve_post: { bg: "#22c55e", Icon: CheckIcon },
  reject_post: { bg: "#ef4444", Icon: XMarkIcon },
  // A course lesson's Discussion tab. One book badge for all of them (amber,
  // like the course accents) so lesson activity reads apart from group posts;
  // the text already says whether it was a reply or a like.
  lesson_post: { bg: "#f59e0b", Icon: BookOpenIcon },
  lesson_reply: { bg: "#f59e0b", Icon: BookOpenIcon },
  lesson_comment_reply: { bg: "#f59e0b", Icon: BookOpenIcon },
  lesson_like_post: { bg: "#f59e0b", Icon: BookOpenIcon },
  lesson_like_comment: { bg: "#f59e0b", Icon: BookOpenIcon },
  lesson_mention: { bg: "#f59e0b", Icon: BookOpenIcon },
};
function TypeGlyph({ type, primary }) {
  const g = NOTIF_GLYPH[type] || { bg: "rgb(var(--c-slate-400))", Icon: BellSolidIcon };
  const bg = g.brand ? primary : g.bg;
  const Icon = g.Icon;
  return (
    <span
      className="absolute -bottom-1 -end-1 flex h-[23px] w-[23px] items-center justify-center rounded-full ring-[2.5px] ring-surface"
      style={{ backgroundColor: bg }}
    >
      <Icon className="h-[13px] w-[13px] text-white" />
    </span>
  );
}

// Header bell with an unread badge + dropdown of recent notifications. Polls the
// unread count; opening the panel loads the list and marks everything read.
export default function NotificationBell({ token, member, primary = "#0274F7" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  // member_token of a chat opened from a message notification; null = closed.
  const [chatTarget, setChatTarget] = useState(null);
  const ref = useRef(null);
  // The mobile full-screen sheet is portalled to <body>, outside `ref` — track
  // it separately so the outside-click handler doesn't treat taps in it as
  // "outside" and close it.
  const mobileRef = useRef(null);
  const memberToken = member?.auth_token;
  const memberId = member?.community_member_id;

  /*
   * The poll is NOT owned by this component.
   *
   * It used to be: each bell ran its own setInterval(20s). The header mounts
   * this component more than once (a desktop row and a phone one, both in the
   * DOM, one shown by CSS), so a page ran two independent pollers — collapsed
   * by apiCache only while their ticks stayed within its 3s window, and split
   * apart for good as soon as one of them re-mounted on a route change.
   *
   * getNotificationPoller() returns ONE poller per signed-in member, shared by
   * every bell on the page. It also owns what a per-component interval could
   * not: pausing while the tab is hidden, backing off when the endpoint keeps
   * failing, and slowing down while the socket is delivering the same events
   * live. See notificationsPoll.js.
   */
  const poller = getNotificationPoller(token, memberToken);

  useEffect(() => {
    if (!poller) return undefined;
    setLoading(true);
    const unsubscribe = poller.subscribe((data) => {
      if (data) {
        // A background tick is count-only and carries no list. Setting it
        // anyway would blank a panel that already has rows in it.
        if (data.notifications) setList(data.notifications);
        setUnread(data.unread);
      }
      // Null = the poll settled without usable data. The spinner still has to
      // come down, or a failing endpoint leaves the panel spinning for ever.
      setLoading(false);
    });
    return unsubscribe;
  }, [poller]);

  // Force a fresh read — panel opened, socket event, chat modal closed.
  const fetchList = useCallback(
    (force = false) => (poller ? poller.refresh() : undefined),
    [poller]
  );

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (mobileRef.current && mobileRef.current.contains(e.target)) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    addOutsidePress(onDoc);
    return () => removeOutsidePress(onDoc);
  }, [open]);

  // Live badge: subscribe to this member's personal channel so a new DM lights
  // up the bell instantly (no waiting for the next poll).
  useEffect(() => {
    if (!token || !memberToken || !memberId) return undefined;
    const echo = getEcho({ communityToken: token, memberToken });
    if (!echo) return undefined;
    const channel = `member.${memberId}`;
    const handler = (payload) => {
      // Our device received it → ack delivery so the sender's ticks go double.
      // This happens even for muted (silent) conversations.
      if (payload?.conversation_id) {
        axios
          .post(`${config.COMMUNITY_API_URL}/membership-chat-delivered`, {
            community_token: token,
            member_token: memberToken,
            conversation_id: payload.conversation_id,
          })
          .catch(() => {});
      }
      // Muted conversations ping silently: ack delivery only, no badge/sound/alert.
      if (payload?.silent) return;
      fetchList(true);
      alertNewMessage(payload?.actor_name || "New message", payload?.preview || "");
    };
    echo.private(channel).listen(".message.notification", handler);

    /*
     * While the socket is up it already pushes message notifications the
     * instant they happen, so the poll is only a safety net for the types it
     * does not push and can run far slower (SOCKET_MS). Told to the poller
     * rather than decided here, because the cadence belongs to the ONE shared
     * poll and not to whichever bell happened to open the channel.
     *
     * Guarded throughout: this reaches into pusher-js internals, and a bell
     * that cannot read the connection state must still poll at the normal rate
     * rather than break.
     */
    let connection = null;
    const onState = () => {
      if (poller) poller.setSocketConnected(connection?.state === "connected");
    };
    try {
      connection = echo.connector?.pusher?.connection || null;
      if (connection) {
        connection.bind("state_change", onState);
        onState();
      }
    } catch (e) {
      /* no connection state available — stay on the normal cadence */
    }

    return () => {
      try {
        if (connection) connection.unbind("state_change", onState);
      } catch (e) {
        /* ignore */
      }
      if (poller) poller.setSocketConnected(false);
      try {
        echo.leave(channel);
      } catch (e) {
        /* ignore */
      }
    };
  }, [token, memberToken, memberId, fetchList, poller]);

  // Mark a single notification read (optimistic local update + persist).
  // poller.mutate() voids any poll already in flight, so the shared read cannot
  // land afterwards and un-read it — the guard is in the poller now, so it
  // protects every bell sharing that poll rather than only this one.
  const markOneRead = (n) => {
    if (n.is_read) return;
    if (poller) poller.mutate();
    setUnread((u) => Math.max(0, u - 1));
    setList((l) => l.map((x) => (x.id === n.id ? { ...x, is_read: 1 } : x)));
    axios
      .post(`${config.COMMUNITY_API_URL}/membership-notifications-read`, {
        community_token: token,
        member_token: memberToken,
        notification_id: n.id,
      })
      .catch(() => {});
  };

  // Where a notification takes you, or null when it isn't clickable.
  //
  // A message opens that conversation in Members Chat. Everything else is about
  // something in a group feed — a new post, a like, a comment or a reply — and
  // lands on it: /membership/{token}/feed?gid={group}&post={id}[&comment={id}],
  // the same deep link the notification emails use. The feed scrolls the post
  // into view and outlines it; with ?comment it opens that post's comments,
  // expands the thread if the target is a reply, and scrolls to it.
  //
  // Older rows stored before the API returned these ids have no group_token and
  // stay unclickable; a rejected post is gone, so that one drops the post id and
  // just opens the feed.
  const notifTarget = (n) => {
    if (n.type === "message") {
      return n.actor_token
        ? `/membership/${token}/memberchat?open=${encodeURIComponent(n.actor_token)}`
        : null;
    }
    // A lesson Discussion notification opens the lesson on its Discussion tab,
    // on the discussion (and reply) it is about — the same link its email uses.
    if (n.context === "lesson") {
      if (!n.course_page_id || !n.lesson_id) return null;
      const lp = new URLSearchParams({ course: n.course_page_id, view: "lessons", lesson: n.lesson_id, tab: "discussion" });
      if (n.post_id) lp.set("post", n.post_id);
      if (n.comment_id) lp.set("comment", n.comment_id);
      return `/membership/${token}/courses?${lp.toString()}`;
    }
    if (!n.group_token) return null;
    const params = new URLSearchParams({ gid: n.group_token });
    if (n.post_id && n.type !== "reject_post") {
      params.set("post", n.post_id);
      // A like on a comment / a comment / a reply all carry the comment id.
      if (n.comment_id) params.set("comment", n.comment_id);
    }
    return `/membership/${token}/feed?${params.toString()}`;
  };

  const openNotification = (n, to) => {
    setOpen(false);
    markOneRead(n);
    navigate(to);
  };

  // Let other parts of the app (e.g. the Members list) open a 1:1 chat with a
  // member by dispatching `membership:open-chat` with { memberToken }. The chat
  // modal (and the server) still enforce group-share / level / block rules.
  useEffect(() => {
    if (!memberId) return undefined;
    const onOpen = (e) => {
      const t = e.detail?.memberToken;
      if (t) setChatTarget(t);
    };
    window.addEventListener("membership:open-chat", onOpen);
    return () => window.removeEventListener("membership:open-chat", onOpen);
  }, [memberId]);

  const markAllRead = async () => {
    if (!unread) return;
    if (poller) poller.mutate();
    setUnread(0);
    setList((l) => l.map((n) => ({ ...n, is_read: 1 })));
    try {
      await axios.post(`${config.COMMUNITY_API_URL}/membership-notifications-read`, {
        community_token: token,
        member_token: memberToken,
      });
    } catch {
      /* ignore */
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      ensureNotificationPermission();
      // Load, but leave items unread until the user clicks a notification or
      // "Mark all as read" (matches the community FE behaviour).
      //
      // This is the read that actually fetches the LIST — the background poll
      // only keeps the badge current — so with nothing to show yet, show the
      // spinner rather than an "all caught up" that is merely not-loaded-yet.
      if (list.length === 0) setLoading(true);
      fetchList(true);
    }
  };

  if (!member) return null;

  // Shared list body — the loading / empty / rows states. Rendered inside both
  // the desktop dropdown and the mobile full-screen sheet.
  const listBody =
    loading && list.length === 0 ? (
      <InlineLoader className="justify-center py-10" primary={primary} />
    ) : list.length === 0 ? (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: `${primary}14` }}>
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke={primary} strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
          </svg>
        </span>
        <div>
          <p className="text-sm font-bold text-gray-800">You&apos;re all caught up</p>
          <p className="mt-0.5 text-xs text-gray-400">New notifications will show up here.</p>
        </div>
      </div>
    ) : (
      <ul className="divide-y divide-gray-50">
        {list.map((n) => {
          const to = notifTarget(n);
          const clickable = !!to;
          return (
            <li
              key={n.id}
              onClick={clickable ? () => openNotification(n, to) : undefined}
              className={`relative flex items-start gap-3 py-3.5 pe-4 transition-colors ${
                n.is_read ? "ps-4 hover:bg-gray-50" : "ps-[13px] hover:bg-black/[0.02]"
              } ${clickable ? "cursor-pointer active:bg-black/[0.04]" : ""}`}
              style={n.is_read ? undefined : { backgroundColor: `${primary}0d` }}
            >
              {/* Full-height accent bar marks an unread notification. */}
              {!n.is_read && (
                <span className="absolute inset-y-0 start-0 w-1" style={{ backgroundColor: primary }} aria-hidden="true" />
              )}
              <ActorAvatar name={n.actor_name} img={n.actor_img} primary={primary} />
              <div className="min-w-0 flex-1">
                <p className={`text-sm leading-snug ${n.is_read ? "text-gray-600" : "font-semibold text-gray-900"}`}>
                  {n.text}
                </p>
                <p className={`mt-0.5 text-xs ${n.is_read ? "text-gray-400" : "font-medium text-gray-500"}`}>
                  {n.time_ago}
                </p>
              </div>
              {!n.is_read && (
                <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-surface" style={{ backgroundColor: primary }} />
              )}
            </li>
          );
        })}
      </ul>
    );

  // One notification row — full-width (Instagram/LinkedIn style): avatar with a
  // kind badge, the actor's name in bold, action text, time, and a "•" thumbnail
  // slot on the right for unread. Unread rows get a light brand wash.
  const notifRow = (n) => {
    const to = notifTarget(n);
    const clickable = !!to;
    const name = n.actor_name || "";
    const rest = name && n.text && n.text.startsWith(name) ? n.text.slice(name.length) : null;
    return (
      <div
        key={n.id}
        onClick={clickable ? () => openNotification(n, to) : undefined}
        className={`flex items-center gap-3.5 px-4 py-3 transition hover:bg-black/[0.02] active:bg-black/[0.04] ${clickable ? "cursor-pointer" : ""}`}
        style={n.is_read ? undefined : { backgroundColor: `${primary}0d` }}
      >
        <span className="relative shrink-0">
          <ActorAvatar name={n.actor_name} img={n.actor_img} primary={primary} size={52} />
          <TypeGlyph type={n.type} primary={primary} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] leading-snug text-gray-700">
            {rest !== null ? (
              <>
                <span className="font-bold text-gray-900">{name}</span>
                <span className={n.is_read ? "text-gray-500" : "text-gray-700"}>{rest}</span>
              </>
            ) : (
              <span className={n.is_read ? "text-gray-600" : "font-medium text-gray-800"}>{n.text}</span>
            )}
          </p>
          <p className="mt-0.5 text-[12px] font-medium text-gray-400">{n.time_ago}</p>
        </div>
        {!n.is_read && (
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: primary, boxShadow: `0 0 0 3px ${primary}1f` }} />
        )}
      </div>
    );
  };

  const sectionLabel = (text) => (
    <p className="bg-surface/80 px-4 pb-1.5 pt-4 text-[12px] font-extrabold uppercase tracking-[0.1em] text-gray-400 backdrop-blur">{text}</p>
  );

  // Grouped full-width feed — New (unread) then Earlier (read).
  const unreadList = list.filter((n) => !n.is_read);
  const readList = list.filter((n) => n.is_read);
  const mobileListBody =
    loading && list.length === 0 ? (
      <InlineLoader className="justify-center py-16" primary={primary} />
    ) : list.length === 0 ? (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-24 text-center">
        <span className="flex h-20 w-20 items-center justify-center rounded-[1.75rem]" style={{ background: `linear-gradient(140deg, ${primary}26 0%, ${primary}0d 100%)` }}>
          <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke={primary} strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
          </svg>
        </span>
        <div>
          <p className="text-[15px] font-bold text-gray-800">You&apos;re all caught up</p>
          <p className="mt-1 text-[13px] text-gray-400">New notifications will show up here.</p>
        </div>
      </div>
    ) : (
      <div className="divide-y divide-gray-100 bg-surface pb-5">
        {unreadList.length > 0 && (
          <div className="divide-y divide-gray-100">
            {sectionLabel("New")}
            {unreadList.map(notifRow)}
          </div>
        )}
        {readList.length > 0 && (
          <div className="divide-y divide-gray-100">
            {sectionLabel(unreadList.length > 0 ? "Earlier" : "Recent")}
            {readList.map(notifRow)}
          </div>
        )}
      </div>
    );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-label="Notifications"
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
      >
        <svg className="h-[22px] w-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -end-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-surface">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Desktop / tablet (≥sm): anchored dropdown */}
      {open && (
        /* `end-0` (inset-inline-end), never `right-0`: with dir="rtl" the bell
           moves to the left edge of the header, and a right-anchored panel opens
           leftwards straight off the viewport (it rendered as an empty sliver).
           The logical property flips with the document direction. Same fix as
           MemberMenu. */
        <div className="absolute end-0 mt-2 z-50 hidden w-80 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-gray-200 bg-surface shadow-2xl sm:block">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
            {/* Only offer the action when something is actually unread. */}
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs font-semibold hover:underline"
                style={{ color: primary }}
              >
                Mark all as read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">{listBody}</div>
        </div>
      )}

      {/* Mobile (<sm): full-screen iOS-style page, portalled above everything */}
      {open &&
        createPortal(
          <div
            ref={mobileRef}
            className="evt-backdrop fixed inset-0 z-[60] flex flex-col bg-surface sm:hidden"
          >
            {/* Nav bar + large title on a subtle brand-tinted band — respects the notch.
                backgroundColor keeps it fully opaque; the gradient only tints it. */}
            <div
              className="shrink-0 border-b border-gray-100 px-3 pt-[max(0.5rem,env(safe-area-inset-top))]"
              style={{ backgroundColor: "rgb(var(--c-surface))", backgroundImage: `linear-gradient(180deg, ${primary}16 0%, rgba(255,255,255,0) 100%)` }}
            >
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Back"
                  className="-ms-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-700 transition active:bg-black/5"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="flex-1" />
                {unread > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    className="shrink-0 rounded-full px-3.5 py-1.5 text-[12px] font-bold text-white shadow-sm transition active:scale-95"
                    style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`, boxShadow: `0 6px 16px -8px ${primary}` }}
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="px-1 pb-4 pt-1">
                <h3 className="text-[27px] font-extrabold leading-tight tracking-tight text-gray-900">Notifications</h3>
                <p className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-gray-400">
                  {unread > 0 ? (
                    <>
                      <span className="inline-flex h-1.5 w-1.5 rounded-full" style={{ backgroundColor: primary }} />
                      <span style={{ color: primary }}>{unread} new</span>
                      {list.length > unread ? ` · ${list.length} total` : ""}
                    </>
                  ) : (
                    "You're all caught up"
                  )}
                </p>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">{mobileListBody}</div>
          </div>,
          document.body
        )}

      {chatTarget && (
        <ChatModal
          token={token}
          member={member}
          primary={primary}
          startWith={chatTarget}
          onClose={() => {
            setChatTarget(null);
            fetchList(true);
          }}
        />
      )}
    </div>
  );
}
